import { API_BASE } from '@/shared/services/api';
import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import { CalendarClock, Clock, RefreshCw } from 'lucide-react';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('ezerpToken')}`
});

export const ShiftsPage = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchShifts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hr/shifts`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch shifts');
      const data = await res.json();
      setShifts(Array.isArray(data) ? data : data.shifts || []);
    } catch (err) {
      toast.error(err.message);
      setShifts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  return (
    <div className="space-y-6" data-testid="shifts-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">Shifts</h1>
          <p className="text-muted-foreground mt-1">Review active shift definitions used by time and attendance.</p>
        </div>
        <Button variant="outline" onClick={fetchShifts} className="gap-2">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-muted-foreground">Active Shifts</p><p className="text-3xl font-bold">{shifts.length}</p></div>
            <div className="p-3 rounded-lg bg-purple-500/20"><CalendarClock className="h-6 w-6 text-purple-500" /></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Shift Registry</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div>
          ) : shifts.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">No shifts found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Code</th>
                    <th className="text-left p-3 font-medium">Name</th>
                    <th className="text-left p-3 font-medium">Start</th>
                    <th className="text-left p-3 font-medium">End</th>
                    <th className="text-left p-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {shifts.map((shift) => (
                    <tr key={shift.id || shift.code} className="hover:bg-slate-800/30">
                      <td className="p-3 font-mono text-sm">{shift.code || '-'}</td>
                      <td className="p-3 font-medium">{shift.name || '-'}</td>
                      <td className="p-3">{shift.start_time || '-'}</td>
                      <td className="p-3">{shift.end_time || '-'}</td>
                      <td className="p-3"><Badge className="bg-green-500/20 text-green-400">active</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ShiftsPage;
